import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Link,
    Paper,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    Fade,
    Stack,
    Divider,
} from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined, MailOutline, KeyboardArrowRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLoginMutation } from '../../services/authApi';
import { setCredentials } from '../../store';
import logo from '../../assets/siera-logo.svg';

const LoginPage = () => {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loginMutation, { isLoading }] = useLoginMutation();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setError('');
    };

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const result = await loginMutation(formData).unwrap();

            dispatch(setCredentials({
                token: result.token,
                refreshToken: result.refreshToken,
                user: result.user,
            }));

            if (result.user.mustChangePassword) {
                navigate('/change-password', { state: { forced: true } });
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            // Error handling
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fcfcfc',
                backgroundImage: `radial-gradient(#e5e7eb 0.5px, transparent 0.5px)`,
                backgroundSize: '24px 24px',
                p: 3,
            }}
        >
            <Fade in={true} timeout={600}>
                <Box sx={{ width: '100%', maxWidth: 440 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 4, sm: 6 },
                            borderRadius: '24px',
                            backgroundColor: '#fff',
                            border: '1px solid #f0f0f0',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.03), 0 20px 25px -5px rgba(0,0,0,0.02)',
                        }}
                    >
                        <Stack spacing={4}>
                            {/* Header Section */}
                            <Stack alignItems="center" spacing={2.5}>
                                <Box
                                    sx={{
                                        width: 90,
                                        height: 90,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '20px',
                                        backgroundColor: '#fff',
                                        boxShadow: '0 8px 16px rgba(0,0,0,0.04)',
                                        border: '1px solid #f5f5f5',
                                        p: 1.5
                                    }}
                                >
                                    <img src={logo} alt="Siera" style={{ width: '100%', height: 'auto' }} />
                                </Box>
                                <Box textAlign="center">
                                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#111', letterSpacing: '-0.02em', mb: 1 }}>
                                        Sign In
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', fontWeight: 400 }}>
                                        Access the Siera HR Management Portal
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* Form Section */}
                            <form onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    {error && (
                                        <Alert severity="error" sx={{ borderRadius: '12px', fontSize: '13px' }}>
                                            {error}
                                        </Alert>
                                    )}

                                    <Stack spacing={1}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#333', ml: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Account Email
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="email"
                                            placeholder="e.g. james@siera.com"
                                            variant="outlined"
                                            value={formData.email}
                                            onChange={handleChange}
                                            disabled={isLoading}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <MailOutline sx={{ color: '#ccc', fontSize: 20 }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    backgroundColor: '#fff',
                                                    transition: '0.2s',
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                    '&.Mui-focused': {
                                                        boxShadow: `0 0 0 4px ${theme.palette.primary.main}10`,
                                                    }
                                                }
                                            }}
                                        />
                                    </Stack>

                                    <Stack spacing={1}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" px={0.5}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Password
                                            </Typography>
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={() => navigate('/forgot-password')}
                                                sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.primary.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                Forgot?
                                            </Link>
                                        </Box>
                                        <TextField
                                            fullWidth
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            variant="outlined"
                                            value={formData.password}
                                            onChange={handleChange}
                                            disabled={isLoading}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlined sx={{ color: '#ccc', fontSize: 20 }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={togglePasswordVisibility} edge="end">
                                                            {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    backgroundColor: '#fff',
                                                    transition: '0.2s',
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                    '&.Mui-focused': {
                                                        boxShadow: `0 0 0 4px ${theme.palette.primary.main}10`,
                                                    }
                                                }
                                            }}
                                        />
                                    </Stack>

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        disabled={isLoading}
                                        endIcon={!isLoading && <KeyboardArrowRight />}
                                        sx={{
                                            py: 1.8,
                                            borderRadius: '14px',
                                            backgroundColor: theme.palette.primary.main,
                                            fontSize: '15px',
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark,
                                                boxShadow: `0 8px 20px ${theme.palette.primary.main}30`,
                                                transform: 'translateY(-1px)',
                                            },
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        }}
                                    >
                                        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Log In to Account'}
                                    </Button>
                                </Stack>
                            </form>

                            <Stack spacing={2} pt={2}>
                                <Divider sx={{ '&::before, &::after': { borderColor: '#f0f0f0' } }}>
                                    <Typography variant="caption" sx={{ color: '#999', fontWeight: 500 }}>
                                        Support
                                    </Typography>
                                </Divider>
                                <Typography textAlign="center" variant="body2" sx={{ color: '#888' }}>
                                    Experience issues?{' '}
                                    <Link href="mailto:it@siera.com" sx={{ color: theme.palette.primary.main, fontWeight: 700, textDecoration: 'none' }}>
                                        Contact IT Support
                                    </Link>
                                </Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Box>
            </Fade>
        </Box>
    );
};

export default LoginPage;
